/**
 * 全域語言處理模組
 *
 * 兩件事：把這一頁實際渲染的語系告訴所有 AJAX 請求，以及在沒有任何偏好時把
 * 瀏覽器語言當成提示送給後端。
 *
 * 這個模組以前還會寫 cookie —— 讀到 `?locale=` 就同步寫入，而且第一次載入時
 * 若 cookie 不存在就把偵測到的瀏覽器語言寫進去。兩者都是「讀一個頁面就改掉
 * 偏好」，而 cookie 的優先序高於 session，所以一個沒人選過的瀏覽器語言會被
 * 當成明示選擇，之後怎麼瀏覽都蓋不掉。偏好現在只由 POST /locale 寫入。
 *
 * 也不再持有支援語系清單。哪些語系有服務是後端的事
 * （config/supported_locales.rb），瀏覽器送出原始的語言標籤，後端
 * normalize_locale 收斂格式、valid_locale? 決定收不收。前端多一份清單只會漂。
 */

const LOCALE_HEADER_NAME = 'X-User-Locale';
const DEFAULT_LOCALE = 'zh-TW'; // 僅供無法取得任何訊號時的最後手段

/**
 * 瀏覽器回報的語言，原樣送出（只把 zh_TW 這種底線格式正規化成連字號）。
 * 收不收由後端決定。
 * @returns {string}
 */
function detectBrowserLocale() {
  try {
    const browserLangs = navigator.languages || [navigator.language];
    const first = browserLangs.find(Boolean);

    return first ? first.replace('_', '-') : DEFAULT_LOCALE;
  } catch (error) {
    console.warn('Failed to detect browser locale:', error);
    return DEFAULT_LOCALE;
  }
}

/**
 * 這一頁實際被渲染成哪個語系。
 *
 * 讀 <html lang>，那是後端解析完的結果，不是重新猜一次。前端自己再跑一遍優先
 * 序只會跟後端不同步 —— 日期格式顯示成一種語言、頁面文字另一種。
 * @returns {string} 語言代碼
 */
function getCurrentUserLocale() {
  try {
    return document.documentElement.lang || detectBrowserLocale();
  } catch (error) {
    console.warn('Failed to read the rendered locale:', error);
    return DEFAULT_LOCALE;
  }
}

/**
 * 設定 fetch 的預設 header 包含語言
 */
function setupFetchHeaders() {
  // 儲存原始的 fetch 函數
  const originalFetch = window.fetch;

  // 重寫 fetch 函數，自動添加語言 header
  window.fetch = function(input, init = {}) {
    const locale = getCurrentUserLocale();

    // 確保 headers 物件存在
    if (!init.headers) {
      init.headers = {};
    }

    // 如果 headers 是 Headers 物件，轉換為普通物件
    if (init.headers instanceof Headers) {
      const headersObj = {};
      init.headers.forEach((value, key) => {
        headersObj[key] = value;
      });
      init.headers = headersObj;
    }

    // 添加語言 header
    init.headers[LOCALE_HEADER_NAME] = locale;

    return originalFetch.call(this, input, init);
  };
}

/**
 * 設定 Turbo 的預設 header 包含語言 - DISABLED (Turbo removed)
 */
function setupTurboHeaders() {
  // Turbo headers disabled as Turbo is removed
}

/**
 * 設定 XMLHttpRequest 的預設 header 包含語言
 */
function setupXHRHeaders() {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
    this._method = method;
    this._url = url;
    return originalOpen.call(this, method, url, async, user, password);
  };

  XMLHttpRequest.prototype.send = function(data) {
    const locale = getCurrentUserLocale();

    // 在發送前設定語言 header
    if (!this.getResponseHeader(LOCALE_HEADER_NAME)) {
      this.setRequestHeader(LOCALE_HEADER_NAME, locale);
    }

    return originalSend.call(this, data);
  };
}

/**
 * 設定 Rails UJS 的語言 header
 */
function setupRailsHeaders() {
  // 為 Rails forms 設定預設 header
  document.addEventListener('ajax:beforeSend', (event) => {
    const locale = getCurrentUserLocale();
    const xhr = event.detail[0];
    xhr.setRequestHeader(LOCALE_HEADER_NAME, locale);
  });
}

/**
 * 初始化全域語言處理
 */
function initializeGlobalLocale() {
  // 不寫 cookie。偏好只由 POST /locale 寫入 —— 見檔頭。
  setupFetchHeaders();
  setupTurboHeaders();
  setupXHRHeaders();
  setupRailsHeaders();
}



/**
 * 檢查語言是否被支援
 * @param {string} locale - 語言代碼
 * @returns {boolean} 是否支援
 */
// 導出函數供外部使用
export {
  initializeGlobalLocale,
  getCurrentUserLocale,
  LOCALE_HEADER_NAME,
  DEFAULT_LOCALE
};
